/* eslint-disable @nx/enforce-module-boundaries */
import { EditIcon } from '@mybridge/icons/Edit';
import { GmailIcon } from '@mybridge/icons/Gmail';
import { Button } from '@mybridge/ui/button';
import { Divider } from '@mybridge/ui/divider';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from '@mybridge/ui/form-control';
import { useToast } from '@mybridge/ui/hooks';
import { IconButton } from '@mybridge/ui/icon-button';
import { Input, InputGroup, InputRightElement } from '@mybridge/ui/input';
import { Box, HStack, Stack } from '@mybridge/ui/layout';
import { Link } from '@mybridge/ui/link';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@mybridge/ui/modal';
import { Text } from '@mybridge/ui/text';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getUserFullName } from 'v4/lib/commons';
import {
  changeEmailRequest,
  openSignInModal,
  resendVerificationEmail,
  setLoggedIn,
  setOpenAuthModal,
} from 'v4/store/actions/auth.actions';
import API from 'v4/store/common/api';
import { APIEndPoints } from 'v4/store/common/endPoint';
import { getUserProfileInfo } from 'v4/store/actions/user-profile.actions';
import { CancelIcon } from '@mybridge/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const ChangeEmailModal = ({ ...props }) => {
  const { t } = useTranslator();
  const { push } = useRouter();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const toast = useToast({
    position: 'top',
    isClosable: true,
  });
  const { userProfileInfo } = useSelector((state) => state.userProfile) ?? {};
  const { email } = userProfileInfo ?? {};
  const onFormSubmit = async ({ email, password }) => {
    const payload = {
      email,
      password,
    };
    setLoading(true);
    try {
      const res = await dispatch(changeEmailRequest(payload));
      const { code, password, email } = res?.payload ?? {};
      if (password || email) {
        toast({
          title: t('activateAccount.invalidEmailPassword') || 'Please enter correct email/password',
          status: 'error',
        });
        return;
      }
      if (code === 'VERIFIED_USER') {
        toast({
          title: t('activateAccount.verifiedUser') || "Verified users can't change email",
          status: 'error',
        });
        return;
      }
      dispatch(getUserProfileInfo());
      dispatch(resendVerificationEmail(payload))
      toast({
        title: t('activateAccount.emailChanged') || 'Email changed successfully!',
        status: 'success',
      });
      dispatch(setOpenAuthModal('send-verify-link'));
    } catch (e) {
      toast({
        title: t('activateAccount.errorSendingLink') || 'Error while sending link!',
        description: e?.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="lg" isCentered {...props} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="12px !important">
        <Box  display="flex" alignItems="center" paddingRight="24px">
        <ModalHeader
          flex="1"
          color="#5B5B5B"
          fontFamily= "Inter"
          fontSize= "20px"
          fontWeight= "700"
          lineHeight= "17.4px"
          textAlign="center"
          marginLeft="29px"
        >{t('activateAccount.editEmail') || "Edit email"}</ModalHeader>
        <Box cursor="pointer" onClick={() => dispatch(setOpenAuthModal('send-verify-link'))}>
        <CancelIcon/>
        </Box>  
        </Box>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <ModalBody marginLeft="-12px" marginRight="-12px" pb={6}>
            <Stack>
              <FormControl isInvalid={errors?.currentEmail}>
                <FormLabel 
               color="#5B5B5B" 
               fontFamily= "Inter"
               fontSize= "16px"
               fontWeight= "400"
               lineHeight= "20.8px" 
                >{t('activateAccount.currentEmail') || "Current email"}*</FormLabel>
                <Input
                  sx={{bg:'#FFFFFF'}}
                  type="email"
                  autoFocus
                  defaultValue={email}
                  placeholder="Current Email"
                  {...register('currentEmail', {
                    required: true,
                    pattern:
                      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  })}
                />
                <FormErrorMessage>{t('createPage.pleaseEnterValidEmail') || "Please enter a valid email"}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={errors?.email}>
                <FormLabel
                color="#5B5B5B" 
                fontFamily= "Inter"
                fontSize= "16px"
                fontWeight= "400"
                lineHeight= "20.8px" 
                >{t('activateAccount.newEmail') || "New email"}*</FormLabel>
                <Input
                  sx={{bg:'#FFFFFF'}}
                  type="email"
                  placeholder={t('activateAccount.newEmail') || "New email"}
                  {...register('email', {
                    required: true,
                    pattern:
                      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  })}
                />
                <FormErrorMessage>{t('createPage.pleaseEnterValidEmail') || "Please enter a valid email"}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={errors?.password}>
                <FormLabel
                color="#5B5B5B" 
                fontFamily= "Inter"
                fontSize= "16px"
                fontWeight= "400"
                lineHeight= "20.8px" 
                >{t('pages.password') || "Password"}*</FormLabel>
                <Input
                  sx={{bg:'#FFFFFF'}}
                  type="password"
                  placeholder={t('pages.password') || "Password"}
                  {...register('password', { required: true })}
                />
                <FormHelperText
                color="#5B5B5B" 
                fontFamily= "Inter"
                fontSize= "16px"
                fontWeight= "400"
                lineHeight= "20.8px" 
                >
                  {t('activateAccount.enterPassword') || "Enter the password you created when joining."}
                </FormHelperText>
                <FormErrorMessage>
                  {t('activateAccount.validPassword') || "Please enter a valid password"}
                </FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter marginLeft="-12px" marginRight="-12px" marginTop="-12px" justifyContent="center">
            <Stack flex={1}>
              <Button
                height="48px"
                borderRadius="30px"
                py={6}
                fontWeight="bold"
                w="100%"
                variant="primary"
                isLoading={loading}
                type="submit"
              >
                {t('activateAccount.changeEmailButton') || "Change Email"}
              </Button>
              <Button
                height="48px"
                borderRadius="30px"
                onClick={(e) => dispatch(setOpenAuthModal('send-verify-link'))}
                py={6}
                fontWeight="bold"
                w="100%"
                variant="ghost"
              >
                {t('buttons.cancel') || "Cancel"}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
